import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "../Buttons/Button";
import TextField from "@mui/material/TextField";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp } from "../../lib/redux";
import { startSendApprovalReviewLinkEmail } from "../../actions/approvalReview";
import {
  APPROVAL_EMAIL_INVALID,
  APPROVAL_EMAIL_SEND_BUTTON,
  APPROVAL_EMAIL_SEND_SENT,
  APPROVAL_REVIEW_EMAIL_LABEL,
  APPROVAL_REVIEWER_NOT_FOUND,
} from "../../i18n/keysTranslations";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import { REGEX_EMAIL } from "../../utils/regex";

const ApprovalReviewEmailSend = ({
  approvalType = "content",
  approvalID = "",
  reviewerID = "",
  defaultEmail = "",
  initialIsSent = false,
  onCallbackEmailSent = () => {},
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const [email, setEmail] = useState(defaultEmail);
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(initialIsSent);
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    setEmail(defaultEmail);
    setIsSent(initialIsSent);
    setValidationError("");
  }, [defaultEmail, initialIsSent, approvalID, approvalType]);

  if (!approvalID) {
    return null;
  }

  const onSendEmail = async () => {
    const normalizedEmail = String(email || "")
      .trim()
      .toLowerCase();

    if (!REGEX_EMAIL.test(normalizedEmail)) {
      setValidationError(t(APPROVAL_EMAIL_INVALID));
      return;
    }

    setValidationError("");
    setIsSending(true);

    const response = await dispatch(
      startSendApprovalReviewLinkEmail({
        approvalType,
        approvalID,
        email: normalizedEmail,
        reviewerID,
      })
    );

    setIsSending(false);

    if (response?.ok) {
      setIsSent(true);
      onCallbackEmailSent({ email: normalizedEmail });
      return;
    }

    if (response?.code === "reviewerNotFound") {
      setValidationError(t(APPROVAL_REVIEWER_NOT_FOUND));
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          mb: 0.75,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            textTransform: "uppercase",
            fontWeight: 700,
            letterSpacing: "0.5px",
            color: "text.secondary",
            fontSize: "0.65rem",
          }}
        >
          {t(APPROVAL_REVIEW_EMAIL_LABEL)}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          border: `1px solid ${INPUT_COLOR_BORDER}`,
          borderRadius: 1.5,
          bgcolor: "background.paper",
          pl: 1.25,
          pr: 0.5,
          py: 0.5,
        }}
      >
        <TextField
          type="email"
          variant="standard"
          value={email}
          disabled={isSending || isSent}
          placeholder="email@ejemplo.com"
          onChange={(event) => {
            setEmail(event.target.value);
            if (validationError) {
              setValidationError("");
            }
          }}
          InputProps={{
            disableUnderline: true,
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            "& .MuiInputBase-root": {
              border: 0,
              boxShadow: "none",
              bgcolor: "transparent",
              minHeight: 32,
              px: 0,
            },
            "& .MuiInputBase-input": {
              fontSize: "0.8rem",
              py: 0.5,
            },
          }}
        />
        <Button
          type="button"
          color="secondary"
          loading={isSending}
          disabled={isSent || !email.trim()}
          onClick={onSendEmail}
          sx={{
            minWidth: "max-content",
            height: 32,
            px: 1.5,
            flexShrink: 0,
            border: "none",
            boxShadow: "none",
          }}
        >
          {t(isSent ? APPROVAL_EMAIL_SEND_SENT : APPROVAL_EMAIL_SEND_BUTTON)}
        </Button>
      </Box>
      {validationError ? (
        <Typography
          variant="caption"
          color="error"
          sx={{ display: "block", mt: 0.5 }}
        >
          {validationError}
        </Typography>
      ) : null}
    </Box>
  );
};

export default ApprovalReviewEmailSend;
